import { Away, EntityKind, PvpFlag, SwitchNote, TICK_MS, VitalsState, flagStateOf, fullName, type EntityState, type ProgressRecord, type PvpStateRecord, type PvpSwitchRecord, type VitalsRecord } from "odyssey-shared";
import { combatantEndsAt } from "./flag-countdown.ts";
import type { HudSwitch } from "./flag-toggle.ts";
import type { FlagState } from "../../ui/kit/FlagMark.tsx";
import { sameLayout, type HudLayout } from "./slots.ts";

/**
 * The HUD's store (P1.9-T13; ADR-061, amendment "the design system lands", "the DOM changes only when a
 * value does"): what the Now HUD shows, owned by the game rather than by React. The page and the scene write
 * into it as things happen, a flag byte read from every frame's snapshot included, and it tells its listeners
 * (React, and the page's report) only when a value changes, so a HUD with nothing new to show draws nothing.
 * Its snapshot is replaced on each change and kept otherwise, as `useSyncExternalStore` needs.
 *
 * How long a reply stays on the message line is counted where it is said, on the scene's clock (ADR-061, "the
 * canvas counts on the scene clock"): the scene sets the line and, when its time runs out, expires it. A line
 * set with no time stays until another replaces it. Why the tab left the world is never a line: the loading
 * shell's disconnect notice says it (P1.9-T15), and the scene clears the line as the page leaves.
 */

/**
 * The player's own exact values as the last `vitals` carried them (ADR-074 decision 5): health, Stamina and Focus
 * with their maximums, whether the character is dead, whether it is in combat and whether it is in combat with a
 * player (`inPvpCombat`, which halves a consumable's heal, ADR-091's amendment of 2026-10-08; absent reads as no).
 */
export interface HudVitals {
  readonly health: number;
  readonly maxHealth: number;
  readonly stamina: number;
  readonly maxStamina: number;
  readonly focus: number;
  readonly maxFocus: number;
  readonly dead: boolean;
  readonly inCombat: boolean;
  readonly inPvpCombat?: boolean;
}

/**
 * The player's target as the target frame shows it (P2.2-T3): its handle, whether it is a character, a creature or a
 * training dummy, its first and family names as the snapshot names it (empty for a thing), its level (null for none),
 * its Health percentage, its PvP flag byte, the staff badge it wears (ADR-112 decision 4.6), and whether the page reads it as one the player may attack, which colours
 * the bar.
 */
export interface HudTarget {
  readonly handle: number;
  readonly kind: "character" | "creature" | "dummy";
  readonly first: string;
  readonly family: string;
  readonly level: number | null;
  readonly health: number;
  readonly flag: number;
  readonly hostile: boolean;
  /** Whether a Game Master has frozen the target (the away state `Held`), which the frame marks; false for a creature and a dummy. */
  readonly held: boolean;
  /** The staff badge the target wears, 0 to 15, from its status byte; 0 for none, for a creature and for a dummy. */
  readonly badge: number;
}

/**
 * The player's own level and XP total as the last `progress` carried them (ADR-083 decision 6).
 */
export interface HudProgress {
  readonly level: number;
  readonly xp: number;
}

/**
 * The connection mark (P1.9-T32, P1.9-T46): the page trying again in the background with the attempt it waits to make
 * of the total, the connection just restored (shown for a moment), a slow one, or an offline one. A slow mark carries no
 * figure: the status slot is 132 logical px wide and the word and a round trip do not fit in it. The first two come from
 * a background reconnect (`setConnection`), the last two from the link watch (`setLink`, `link-watch.ts`).
 */
export type HudConnection = { readonly state: "reconnecting"; readonly attempt: number; readonly total: number } | { readonly state: "restored" } | { readonly state: "slow" } | { readonly state: "offline" };

/**
 * What the HUD shows: the character's full name and its first name alone, which the player card draws, the PvP flag
 * byte, when the player's Combatant window ends on the page clock (null for none) and its PvP preference from the last
 * `pvpState`, its flag switch from the last `pvpSwitch` (ADR-076), the character's level (null until its own entity
 * arrives), the message line, the player's own vitals (null before the first `vitals`), its own effects' summed
 * maximum-health modifier from the last `effects` frame (0 for none, ADR-079's amendment for P2.2-T16), whether the health bar
 * shows its value (the viewer's setting, on by default), whether the ability bar's slots draw their keys (the viewer's
 * setting, off by default, P2.18-T15), whether motion is reduced (the game's setting, R-0061), by
 * which the card times its flashes, whether the flag toggle's panel is open (its mark, a click and the U key share it,
 * ADR-076's amendment of 2026-10-01) and whether a key opened it, which makes Enter press its button (its note of
 * 2026-10-03), the player's target (null for none, P2.2-T3), its level and XP from the last
 * `progress` (null before the first, P2.4-T3), the connection mark (null while the connection holds, P1.9-T32), and the
 * area it is laid out on.
 */
export interface HudSnapshot {
  readonly name: string;
  readonly firstName: string;
  readonly flag: number;
  readonly combatantEndsAt: number | null;
  readonly preference: number;
  readonly pvpSwitch: HudSwitch;
  readonly level: number | null;
  readonly message: string | null;
  /** The prompt of a door entered with E, drawn in a place of its own over the message line, which no reply replaces. */
  readonly prompt: string | null;
  readonly vitals: HudVitals | null;
  readonly maxHealthPercent: number;
  readonly healthNumbers: boolean;
  readonly barKeys: boolean;
  readonly reducedMotion: boolean;
  readonly flagPanel: boolean;
  readonly flagPanelByKey: boolean;
  readonly inParty: boolean;
  readonly target: HudTarget | null;
  readonly progress: HudProgress | null;
  readonly connection: HudConnection | null;
  readonly layout: HudLayout;
}

/**
 * What the HUD's view reads: what to show, and a way to hear that it changed. Both work unbound, as React
 * calls them.
 */
export interface HudSource {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => HudSnapshot;
}

/**
 * What the scene tells the HUD: the character's name from `welcome`, the replies it shows on the message line,
 * the local character's flag byte and level, and its vitals.
 */
export interface HudFeed {
  /**
   * Sets the character's name the vitals frame shows: the two parts `welcome` carries, the same the map server names
   * the character's entity with, so the vitals frame and the nameplate agree (ADR-067 decision 4). The card draws the
   * first name alone and keeps the full name as its title and accessible name (ADR-061's amendment of 2026-10-03).
   *
   * @param firstName The first name, or empty for no character.
   * @param familyName The family name, empty or left out for none.
   */
  setName(firstName: string, familyName?: string): void;
  /**
   * Shows a line on the message line, replacing any line there, or clears it.
   *
   * @param text The line, or null (or empty) for none.
   */
  setMessage(text: string | null): void;
  /**
   * Shows the connection mark, or clears it (P1.9-T32): the page's background reconnect, the attempt it waits to make,
   * or the connection back.
   *
   * @param connection What the mark shows, or null for none.
   */
  setConnection(connection: HudConnection | null): void;
  /**
   * Sets the state of the link itself, Slow connection or Offline, or clears it (P1.9-T46). The mark shows it only
   * while no background reconnect has a mark of its own: a reconnect's mark wins.
   *
   * @param link Slow or offline, or null for a healthy link.
   */
  setLink(link: HudConnection | null): void;
  /**
   * Shows the prompt of a door entered with E, in its own place over the message line, or clears it. A reply on the message
   * line neither replaces nor blanks it.
   *
   * @param text The prompt, or null (or empty) for none.
   */
  setPrompt(text: string | null): void;
  /**
   * Clears the message line if it still shows a line, as its time runs out; a line that has replaced it
   * stays.
   *
   * @param text The line whose time ran out.
   */
  expire(text: string): void;
  /**
   * Sets the local character's PvP flag byte.
   *
   * @param byte The byte from the snapshot (Bible 6.1): the state, with the zone lock in its top bit.
   */
  setFlag(byte: number): void;
  /**
   * Sets the local character's level from its own entity's Level component (ADR-074 decision 8).
   *
   * @param level The level, 1 to 255; 0 (a thing's) or anything else not a whole number from 1 is no level.
   */
  setLevel(level: number): void;
  /**
   * Sets the player's own exact values from a `vitals` frame.
   *
   * @param vitals The decoded frame; its state bits beyond dead and in combat are ignored.
   */
  setVitals(vitals: Readonly<VitalsRecord>): void;
  /**
   * Sets the player's own effects' summed maximum-health modifier, from the last `effects` frame
   * (`frameMaxHealthPercent`).
   *
   * @param percent Whole percents, 0 for none.
   */
  setMaxHealthPercent(percent: number): void;
  /**
   * Sets the player's target from the state the client's view holds for it, as the scene does every frame, telling
   * the listeners only when something the frame shows changed.
   *
   * @param state The target's state, or null for none.
   * @param hostile Whether the page reads it as one the player may attack.
   * @param first The name the frame shows, the state's first name unless the page names it otherwise, as a creature's
   *   look does (ADR-082 decision 5).
   */
  setTarget(state: Readonly<EntityState> | null, hostile: boolean, first?: string): void;
  /**
   * Sets the player's Combatant window and PvP preference from a `pvpState` frame.
   *
   * @param pvp The decoded frame.
   * @param nowMs The page clock at receipt (`performance.now()`).
   */
  setPvpState(pvp: Readonly<PvpStateRecord>, nowMs: number): void;
  /**
   * Sets the player's flag switch from a `pvpSwitch` frame (ADR-076 decision 4).
   *
   * @param pvpSwitch The decoded frame.
   * @param nowMs The page clock at receipt (`performance.now()`).
   */
  setPvpSwitch(pvpSwitch: Readonly<Omit<PvpSwitchRecord, "instant"> & Partial<Pick<PvpSwitchRecord, "instant">>>, nowMs: number): void;

  /**
   * Sets whether the player is seated in a party of two or more, which locks the flag panel's switch (ADR-076's
   * amendment of 2026-10-08, E2). Listeners are told only when the value changes.
   *
   * @param inParty True while the player holds a seat.
   */
  setInParty(inParty: boolean): void;
  /**
   * Sets the player's level and XP from a `progress` frame (ADR-083 decision 6).
   *
   * @param progress The decoded frame.
   */
  setProgress(progress: Readonly<ProgressRecord>): void;
  /**
   * Binds the way a flag toggle request reaches the server: the session's connection, while there is one.
   *
   * @param send Sends a `pvpFlagToggle` for a preference, answering whether it went.
   */
  bindFlagToggle(send: (wanted: number) => boolean): void;
  /**
   * The preference the player last asked for, so a refusal names the switch it refused.
   *
   * @returns `PvpFlag.Protected` or `PvpFlag.Flagged`.
   */
  readonly lastRequested: number;
}

/**
 * No switch counting and no note.
 */
const NO_SWITCH: HudSwitch = { endsAt: null, toward: PvpFlag.Protected, note: SwitchNote.None, instant: false };

/**
 * The store.
 *
 * @example
 * ```ts
 * const hud = new HudStore(hudLayout(view));
 * hud.subscribe(() => render(hud.getSnapshot()));
 * hud.setFlag(0x81);
 * ```
 */
export class HudStore implements HudSource, HudFeed {
  private snapshot: HudSnapshot;
  private reconnectMark: HudConnection | null = null;
  private linkMark: HudConnection | null = null;
  private readonly listeners = new Set<() => void>();
  private sendToggle: (wanted: number) => boolean = () => false;
  private requested: number = PvpFlag.Protected;

  /**
   * @param layout The area the HUD is laid out on at first.
   */
  public constructor(layout: HudLayout) {
    this.snapshot = { name: "", firstName: "", flag: PvpFlag.Protected, combatantEndsAt: null, preference: PvpFlag.Protected, pvpSwitch: NO_SWITCH, level: null, message: null, prompt: null, vitals: null, maxHealthPercent: 0, healthNumbers: true, barKeys: false, reducedMotion: false, flagPanel: false, flagPanelByKey: false, inParty: false, target: null, progress: null, connection: null, layout };
  }

  /**
   * Adds a listener told of every change. Works unbound, as React calls it.
   *
   * @param listener Called after each change.
   * @returns A function that removes it.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * What the HUD shows now: the same object until something changes. Works unbound, as React calls it.
   *
   * @returns The snapshot.
   */
  public readonly getSnapshot = (): HudSnapshot => this.snapshot;

  /**
   * Sets the character's name the vitals frame shows.
   *
   * @param firstName The first name, or empty for no character.
   * @param familyName The family name, empty or left out for none.
   */
  public setName(firstName: string, familyName = ""): void {
    const name = fullName(firstName, familyName);
    if (name !== this.snapshot.name || firstName !== this.snapshot.firstName) {
      this.change({ ...this.snapshot, name, firstName });
    }
  }

  /**
   * Sets the local character's PvP flag byte, as the scene does every frame.
   *
   * @param byte The byte.
   */
  public setFlag(byte: number): void {
    if (byte !== this.snapshot.flag) {
      this.change({ ...this.snapshot, flag: byte });
    }
  }

  /**
   * Sets the local character's level, as the scene does every frame from its own entity, telling the listeners
   * only when it changes.
   *
   * @param level The level; one that is not a whole number from 1 is no level.
   */
  public setLevel(level: number): void {
    const next = Number.isInteger(level) && level >= 1 ? level : null;
    if (next !== this.snapshot.level) {
      this.change({ ...this.snapshot, level: next });
    }
  }

  /**
   * Sets whether the player card's health bar shows its value, telling the listeners only when it changes.
   *
   * @param show Whether the value shows.
   */
  public setHealthNumbers(show: boolean): void {
    if (show !== this.snapshot.healthNumbers) {
      this.change({ ...this.snapshot, healthNumbers: show });
    }
  }

  /**
   * Sets whether the ability bar's slots draw their keys, telling the listeners only when it changes.
   *
   * @param show Whether the keys draw.
   */
  public setBarKeys(show: boolean): void {
    if (show !== this.snapshot.barKeys) {
      this.change({ ...this.snapshot, barKeys: show });
    }
  }

  /**
   * Sets whether motion is reduced, as the page's reduced motion setting answers, telling the listeners only when it
   * changes, so the card starts its flashes again in the page clock's phase when they run again.
   *
   * @param reduced Whether motion is reduced.
   */
  public setReducedMotion(reduced: boolean): void {
    if (reduced !== this.snapshot.reducedMotion) {
      this.change({ ...this.snapshot, reducedMotion: reduced });
    }
  }

  /**
   * Sets the player's own exact values from a `vitals` frame, telling the listeners only when one of them changed.
   *
   * @param vitals The decoded frame.
   */
  public setVitals(vitals: Readonly<VitalsRecord>): void {
    const dead = (vitals.state & VitalsState.Dead) !== 0;
    const inCombat = (vitals.state & VitalsState.InCombat) !== 0;
    const inPvpCombat = (vitals.state & VitalsState.InPvpCombat) !== 0;
    const current = this.snapshot.vitals;
    if (current !== null && current.health === vitals.health && current.maxHealth === vitals.maxHealth && current.stamina === vitals.stamina && current.maxStamina === vitals.maxStamina && current.focus === vitals.focus && current.maxFocus === vitals.maxFocus && current.dead === dead && current.inCombat === inCombat && (current.inPvpCombat ?? false) === inPvpCombat) {
      return;
    }
    this.change({ ...this.snapshot, vitals: { health: vitals.health, maxHealth: vitals.maxHealth, stamina: vitals.stamina, maxStamina: vitals.maxStamina, focus: vitals.focus, maxFocus: vitals.maxFocus, dead, inCombat, inPvpCombat } });
  }

  /**
   * Sets the player's own effects' summed maximum-health modifier, telling the listeners only when it changes.
   *
   * @param percent Whole percents, 0 for none; anything not a whole number is none.
   */
  public setMaxHealthPercent(percent: number): void {
    const next = Number.isInteger(percent) ? percent : 0;
    if (next !== this.snapshot.maxHealthPercent) {
      this.change({ ...this.snapshot, maxHealthPercent: next });
    }
  }

  /**
   * Sets the player's target, comparing each value the frame shows with the one held, so a frame that changes nothing
   * allocates nothing and tells nobody.
   *
   * @param state The target's state, or null for none.
   * @param hostile Whether the page reads it as one the player may attack.
   * @param shown The name the frame shows, the state's first name unless the page names it otherwise, as a creature's
   *   look does (ADR-082 decision 5): a creature carries no name on the wire, and one whose look names none shows its
   *   level alone.
   */
  public setTarget(state: Readonly<EntityState> | null, hostile: boolean, shown?: string): void {
    const current = this.snapshot.target;
    if (state === null) {
      if (current !== null) {
        this.change({ ...this.snapshot, target: null });
      }
      return;
    }
    const kind = state.kind === EntityKind.Player ? "character" : state.kind === EntityKind.Dummy ? "dummy" : "creature";
    const first = shown ?? state.name.first;
    const family = state.name.family;
    const level = kind !== "dummy" && Number.isInteger(state.level) && state.level >= 1 ? state.level : null;
    const held = kind === "character" && state.away === Away.Held;
    const badge = kind === "character" ? (state.badge ?? 0) : 0;
    if (current !== null && current.held === held && current.badge === badge && current.handle === state.handle && current.kind === kind && current.first === first && current.family === family && current.level === level && current.health === state.health && current.flag === state.pvp && current.hostile === hostile) {
      return;
    }
    this.change({ ...this.snapshot, target: { handle: state.handle, kind, first, family, level, health: state.health, flag: state.pvp, hostile, held, badge } });
  }

  /**
   * Sets the player's Combatant window's end on the page clock and its PvP preference from a `pvpState` frame. A frame
   * naming a window always tells the listeners, since it names a new end; one with no window and the same preference
   * as before changes nothing.
   *
   * @param pvp The decoded frame.
   * @param nowMs The page clock at receipt (`performance.now()`).
   */
  public setPvpState(pvp: Readonly<PvpStateRecord>, nowMs: number): void {
    const endsAt = combatantEndsAt(nowMs, pvp.combatantTicks);
    if (endsAt === this.snapshot.combatantEndsAt && pvp.preference === this.snapshot.preference) {
      return;
    }
    this.change({ ...this.snapshot, combatantEndsAt: endsAt, preference: pvp.preference });
  }

  /**
   * Sets the player's flag switch from a `pvpSwitch` frame: when it ends on the page clock (the receipt time plus the
   * ticks left, null when none counts), the preference it counts toward and the note. Every frame tells the listeners,
   * since each is sent for a change, a refusal repeated included.
   *
   * @param pvpSwitch The decoded frame.
   * @param nowMs The page clock at receipt (`performance.now()`).
   */
  public setPvpSwitch(pvpSwitch: Readonly<Omit<PvpSwitchRecord, "instant"> & Partial<Pick<PvpSwitchRecord, "instant">>>, nowMs: number): void {
    const endsAt = pvpSwitch.ticks > 0 ? nowMs + pvpSwitch.ticks * TICK_MS : null;
    this.change({ ...this.snapshot, pvpSwitch: { endsAt, toward: endsAt === null ? PvpFlag.Protected : pvpSwitch.toward, note: pvpSwitch.note, instant: pvpSwitch.instant === true } });
  }

  /**
   * Sets the player's level and XP from a `progress` frame, telling the listeners only when either changed.
   *
   * @param progress The decoded frame; what it gained and why are the page's to say, not the HUD's to keep.
   */
  public setProgress(progress: Readonly<ProgressRecord>): void {
    const before = this.snapshot.progress;
    if (before === null || before.level !== progress.level || before.xp !== progress.xp) {
      this.change({ ...this.snapshot, progress: { level: progress.level, xp: progress.xp } });
    }
  }

  /**
   * Opens or closes the flag toggle's panel, keeping whether a key opened it; a closed panel was opened by none. The
   * same state again changes nothing the HUD draws.
   *
   * @param open True to open it.
   * @param byKey True when a key opens it (the U key), false for the card's mark.
   */
  public setFlagPanel(open: boolean, byKey = false): void {
    const keyed = open && byKey;
    if (open !== this.snapshot.flagPanel || keyed !== this.snapshot.flagPanelByKey) {
      this.change({ ...this.snapshot, flagPanel: open, flagPanelByKey: keyed });
    }
  }

  /**
   * Binds the way a flag toggle request reaches the server.
   *
   * @param send Sends a `pvpFlagToggle`, answering whether it went.
   */
  public bindFlagToggle(send: (wanted: number) => boolean): void {
    this.sendToggle = send;
  }

  public setInParty(inParty: boolean): void {
    if (inParty !== this.snapshot.inParty) {
      this.change({ ...this.snapshot, inParty });
    }
  }

  /**
   * Asks the server for a preference, as the toggle's button does: the page changes nothing it shows until the server
   * answers in `pvpSwitch`.
   *
   * @param wanted The preference wanted, `PvpFlag.Protected` or `PvpFlag.Flagged`.
   * @returns Whether the request went.
   */
  public requestFlag(wanted: number): boolean {
    this.requested = wanted;
    return this.sendToggle(wanted);
  }

  /**
   * The preference the player last asked for.
   *
   * @returns `PvpFlag.Protected` or `PvpFlag.Flagged`.
   */
  public get lastRequested(): number {
    return this.requested;
  }

  /**
   * Shows the prompt of a door entered with E, or clears it. The same prompt again changes nothing the HUD draws.
   *
   * @param text The prompt, or null (or empty) for none.
   */
  public setPrompt(text: string | null): void {
    const prompt = text === "" ? null : text;
    if (prompt !== this.snapshot.prompt) {
      this.change({ ...this.snapshot, prompt });
    }
  }

  /**
   * Shows a line on the message line, or clears it. The same line again changes nothing the HUD draws.
   *
   * @param text The line, or null (or empty) for none.
   */
  public setMessage(text: string | null): void {
    const message = text === "" ? null : text;
    if (message !== this.snapshot.message) {
      this.change({ ...this.snapshot, message });
    }
  }

  /**
   * Shows the connection mark, or clears it. The same attempt again changes nothing the HUD draws.
   *
   * @param connection What the mark shows, or null for none.
   */
  public setConnection(connection: HudConnection | null): void {
    this.reconnectMark = connection;
    this.showConnection();
  }

  /**
   * Sets the link's own state, which shows under any reconnect mark.
   *
   * @param link Slow or offline, or null for a healthy link.
   */
  public setLink(link: HudConnection | null): void {
    this.linkMark = link;
    this.showConnection();
  }

  /**
   * Shows the reconnect's mark when it has one, else the link's, telling the listeners only when what is drawn changes.
   */
  private showConnection(): void {
    const connection = this.reconnectMark ?? this.linkMark;
    const held = this.snapshot.connection;
    const same = connection === null ? held === null : held !== null && sameConnection(held, connection);
    if (!same) {
      this.change({ ...this.snapshot, connection });
    }
  }

  /**
   * Clears the message line if it still shows the line whose time ran out.
   *
   * @param text The line.
   */
  public expire(text: string): void {
    if (this.snapshot.message === text) {
      this.change({ ...this.snapshot, message: null });
    }
  }

  /**
   * Sets the area the HUD is laid out on, as the window or its device pixel ratio changes, or the viewer's Ability bar
   * choice (`barFive`).
   *
   * @param layout The area and the UI scale.
   */
  public setLayout(layout: HudLayout): void {
    const current = this.snapshot.layout;
    if (!sameLayout(layout, current)) {
      this.change({ ...this.snapshot, layout });
    }
  }

  /**
   * Replaces the snapshot and tells every listener.
   *
   * @param next The new snapshot.
   */
  private change(next: HudSnapshot): void {
    this.snapshot = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}

/**
 * Whether two connection marks draw the same.
 *
 * @param a One mark.
 * @param b The other.
 * @returns True when they have the same state and, for an attempt, the same figures.
 */
export function sameConnection(a: HudConnection, b: HudConnection): boolean {
  if (a.state === "reconnecting") {
    return b.state === "reconnecting" && a.attempt === b.attempt && a.total === b.total;
  }
  return a.state === b.state;
}

/**
 * A PvP flag byte as the vitals frame shows it: the state, and whether an area holds it (Bible 6.1). A state
 * the byte does not name reads as Protected, as the HUD has always read it.
 *
 * @param byte The byte: the state in its low six bits, the switching bit, which the mark does not read, and the zone lock in its top bit.
 * @returns The state and the lock.
 * @example
 * flagOf(0x81);
 * // { state: "flagged", locked: true }
 */
export function flagOf(byte: number): { readonly state: FlagState; readonly locked: boolean } {
  const state = flagStateOf(byte);
  return { state: state === PvpFlag.Combatant ? "combatant" : state === PvpFlag.Flagged ? "flagged" : "protected", locked: (byte & PvpFlag.ZoneLocked) !== 0 };
}
